import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IPersonRow, isFamilyRelationship } from './prototypeData';

// ─────────────────────────────────────────────────────────────────────────────
// IH-6912 spike — fake care-map edit page.
//
// Shown when a row's "Update in Care Map" menu item is tapped. Mirrors the
// real Edit Inner Circle Member / Edit Community Support drawer visually but
// is entirely throwaway:
//   - All fields are pre-filled with the row's mock data and read-only-ish.
//   - Save / Delete fire stub callbacks.
//   - Adds a prominent "Access" banner up top (NEW vs. the real page) showing
//     the person's viewer/co-owner role with a Manage link back to Sharing & Access.
// ─────────────────────────────────────────────────────────────────────────────

const useStyles = createUseStyles({
    page: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        paddingBottom: 4
    },
    headerBtn: {
        background: 'none',
        border: 0,
        color: 'var(--ion-color-primary)',
        fontSize: 18,
        cursor: 'pointer',
        padding: 4,
        lineHeight: 1
    },
    headerTitle: {
        flex: 1,
        fontSize: 17,
        fontWeight: 700,
        color: 'var(--ion-text-color)',
        textAlign: 'center'
    },

    fakeFlag: {
        fontSize: 11,
        color: 'var(--ion-color-medium)',
        fontStyle: 'italic',
        textAlign: 'center',
        marginTop: -8
    },

    // Access banner — sits up top, prominent, with the manage link.
    accessBanner: {
        background: 'rgba(17,120,157,0.08)',
        border: '1px solid rgba(17,120,157,0.18)',
        borderRadius: 10,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10
    },
    accessBannerText: {
        flex: 1,
        fontSize: 13,
        color: 'var(--ion-text-color)',
        lineHeight: 1.4
    },
    accessBannerRole: {
        fontWeight: 700
    },
    accessBannerManage: {
        background: 'none',
        border: 0,
        color: 'var(--ion-color-primary)',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        padding: 0,
        whiteSpace: 'nowrap'
    },

    avatarRow: {
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        marginTop: 6
    },
    heartAvatar: {
        width: 52,
        height: 52,
        borderRadius: '50%',
        background: 'rgba(238,108,78,0.12)',
        color: 'var(--ion-color-secondary)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 22,
        flexShrink: 0
    },

    field: {
        position: 'relative',
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 8,
        padding: '14px 12px 10px',
        background: '#fff'
    },
    fieldLabel: {
        position: 'absolute',
        top: -8,
        left: 10,
        background: '#fff',
        padding: '0 6px',
        fontSize: 11,
        color: 'var(--ion-color-step-750)'
    },
    fieldValue: {
        fontSize: 14,
        color: 'var(--ion-text-color)',
        minHeight: 18
    },
    fieldEmpty: {
        fontSize: 14,
        color: 'var(--ion-color-step-400)'
    },
    fieldValueWithIcon: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    fieldClear: {
        marginLeft: 'auto',
        color: 'var(--ion-color-step-400)',
        background: 'none',
        border: 0,
        fontSize: 16,
        cursor: 'pointer',
        padding: 0
    },
    fieldSearchIcon: {
        color: 'var(--ion-color-step-400)',
        fontSize: 14
    },

    deleteBtn: {
        marginTop: 10,
        background: '#fff',
        color: 'var(--ion-color-secondary)',
        border: '1px solid var(--ion-color-secondary)',
        borderRadius: 8,
        padding: '11px',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer'
    },

    footer: {
        position: 'sticky',
        bottom: 0,
        background: '#fff',
        paddingTop: 12,
        paddingBottom: 4,
        marginTop: 12,
        borderTop: '1px solid var(--ion-color-step-100)',
        display: 'flex',
        gap: 10,
        zIndex: 1
    },
    cancelBtn: {
        flex: 1,
        background: '#fff',
        border: '1px solid var(--ion-color-primary)',
        color: 'var(--ion-color-primary)',
        borderRadius: 8,
        padding: '12px',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer'
    },
    saveBtn: {
        flex: 1,
        background: 'var(--ion-color-primary)',
        border: 0,
        color: '#fff',
        borderRadius: 8,
        padding: '12px',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer'
    }
});

export interface IPrototypeCareMapEditDrawerProps {
    row: IPersonRow;
    patientFirstName: string;
    onClose: () => void;
    onManageAccess: () => void;
    onStubAction: (message: string) => void;
}

export const PrototypeCareMapEditDrawer: FC<IPrototypeCareMapEditDrawerProps> = ({
    row,
    patientFirstName,
    onClose,
    onManageAccess,
    onStubAction
}) => {
    const classes = useStyles();
    const isFamily = isFamilyRelationship(row.careMapRole);

    const title = isFamily ? 'Edit Inner Circle Member' : 'Edit Community Support';
    const deleteLabel = isFamily ? 'Delete Inner Circle Member' : 'Delete Community Support';
    const typeFieldLabel = isFamily ? `Relationship to ${patientFirstName}` : 'Support, Group, or Activity Type';

    return (
        <div className={classes.page}>
            <div className={classes.header}>
                <button className={classes.headerBtn} onClick={onClose} aria-label="Back">‹</button>
                <div className={classes.headerTitle}>{title}</div>
                <button className={classes.headerBtn} onClick={onClose} aria-label="Close">✕</button>
            </div>
            <div className={classes.fakeFlag}>This page is a prototype — nothing here actually saves.</div>

            <div className={classes.accessBanner}>
                <div className={classes.accessBannerText}>
                    <span className={classes.accessBannerRole}>{row.name}</span> has{' '}
                    <span className={classes.accessBannerRole}>{row.role}</span> access.
                </div>
                <button className={classes.accessBannerManage} onClick={onManageAccess}>
                    Manage ›
                </button>
            </div>

            <div className={classes.avatarRow}>
                <div className={classes.heartAvatar}>♥</div>
                <div style={{ flex: 1 }}>
                    <div className={classes.field}>
                        <span className={classes.fieldLabel}>Support Name</span>
                        <div className={classes.fieldValue}>{row.name}</div>
                    </div>
                </div>
            </div>

            <div className={classes.field}>
                <span className={classes.fieldLabel}>{typeFieldLabel}</span>
                <div className={classes.fieldValueWithIcon}>
                    <span className={classes.fieldSearchIcon}>🔍</span>
                    <span className={classes.fieldValue}>{row.careMapRole || ''}</span>
                    <button
                        className={classes.fieldClear}
                        onClick={() => onStubAction('Stub: would clear the relationship.')}
                        aria-label="Clear"
                    >
                        ✕
                    </button>
                </div>
            </div>

            <div className={classes.field}>
                <span className={classes.fieldLabel}>Organization</span>
                <div className={classes.fieldEmpty}>&nbsp;</div>
            </div>

            <div className={classes.field}>
                <span className={classes.fieldLabel}>Address</span>
                <div className={classes.fieldEmpty}>&nbsp;</div>
            </div>

            <div className={classes.field}>
                <span className={classes.fieldLabel}>Email</span>
                {row.email
                    ? <div className={classes.fieldValue}>{row.email}</div>
                    : <div className={classes.fieldEmpty}>name@example.com</div>}
            </div>

            <div className={classes.field}>
                <span className={classes.fieldLabel}>Cell Phone</span>
                <div className={classes.fieldEmpty}>999-999-9999</div>
            </div>

            <div className={classes.field}>
                <span className={classes.fieldLabel}>Work Phone</span>
                <div className={classes.fieldEmpty}>999-999-9999 x999</div>
            </div>

            <button
                className={classes.deleteBtn}
                onClick={() => onStubAction(`Stub: would ${deleteLabel.toLowerCase()}.`)}
            >
                {deleteLabel}
            </button>

            <div className={classes.footer}>
                <button className={classes.cancelBtn} onClick={onClose}>Cancel</button>
                <button
                    className={classes.saveBtn}
                    onClick={() => {
                        onStubAction('Stub: would save changes.');
                        onClose();
                    }}
                >
                    Save
                </button>
            </div>
        </div>
    );
};
